import {
  createMapbox,
  addDestinationMarkers,
  addRouteLines,
  hideMapTitles,
  addInteractiveMapStaticRouteLines,
} from '@/utils/mapbox/create-mapbox'
import { observerVisibility } from '@/utils/mapbox/observe-visibility'
import { createMapboxControls } from '@/utils/mapbox/mapbox-controls'
import { waitForMapbox } from '@/utils/mapbox/mapbox-global'
import type { Map } from 'mapbox-gl'
import type { MapComponentConfig } from '@/utils/mapbox/mapbox-component-config'
import type {
  InteractiveMapBlock,
  MapBlock,
  MapBlockComponent,
} from '@/types/map/map.types'
import { generateBoundsFromRoutes } from '@/utils/mapbox/coordinate-converters'

export const baseMapContextState = ({
  hideMapStats,
  options,
}: Pick<MapComponentConfig, 'hideMapStats' | 'options'>) => ({
  mapbox: null as Map | null,
  inView: false,
  hideMapStats,
  observer: null as IntersectionObserver | null,
  options,
})

export const baseMapInit = (
  alpineContext: MapBlockComponent<MapBlock | InteractiveMapBlock>,
) => {
  alpineContext.observer = observerVisibility(
    alpineContext.$el.id,
    alpineContext.initMapbox.bind(alpineContext),
    alpineContext.destroyMapbox.bind(alpineContext),
    { threshold: 0, rootMargin: '35%' },
  ) as any
}

export const baseMapDestroy = (
  alpineContext: MapBlockComponent<MapBlock | InteractiveMapBlock>,
) => {
  alpineContext.observer && alpineContext.observer.disconnect()
  alpineContext.destroyMapbox()
}

export const baseInitMapbox = async (
  alpineContext: MapBlockComponent<MapBlock | InteractiveMapBlock>,
  {
    camera,
    destinations,
    routes,
    isInteractiveMap,
  }: Pick<
    MapComponentConfig,
    'camera' | 'destinations' | 'routes' | 'isInteractiveMap'
  >,
) => {
  // Wait for Mapbox to be available since it's loaded asynchronously
  await waitForMapbox()

  const {
    accessToken,
    mapStyle,
    containerId,
    animateRoute,
    enableTouch,
    showControls,
    showMarkers,
    showRoute,
    shouldHideMapTitles,
    cooperativeGestures,
    scrollZoom,
    touchZoomRotate,
    showMarkersLabels,
  } = alpineContext.options as any

  alpineContext.mapbox = createMapbox({
    accessToken,
    mapStyle,
    containerId,
    camera,
    enableTouch,
    scrollZoom,
    ...(touchZoomRotate && { touchZoomRotate }),
    ...(cooperativeGestures && { cooperativeGestures }),
  })

  const bounds = generateBoundsFromRoutes(destinations)
  alpineContext.bounds = bounds

  showControls && createMapboxControls(alpineContext.mapbox)
  showMarkers &&
    addDestinationMarkers(alpineContext.mapbox, destinations, {
      showLabels: showMarkersLabels,
    })

  shouldHideMapTitles && hideMapTitles(alpineContext.mapbox)

  isInteractiveMap
    ? addInteractiveMapStaticRouteLines(alpineContext.mapbox, routes)
    : showRoute && addRouteLines(alpineContext.mapbox, routes, animateRoute)
}

export const baseDestroyMapbox = (
  alpineContext: MapBlockComponent<MapBlock | InteractiveMapBlock>,
) => {
  alpineContext.mapbox && alpineContext.mapbox.remove()
}
